import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import {
  ContactMail as ContactMailIcon,
  VerifiedUser as VerifiedUserIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ErrorOutline as ErrorOutlineIcon,
  Gavel as GavelIcon,
  Info as InfoIcon,
  Handshake as HandshakeIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FullOnboardingForm, fullOnboardingSchema } from "lib/schema";

import { WelcomeStep } from "./steps/WelcomeStep";
import { ContactInfoStep } from "./steps/ContactInfoStep";
import { TermsAgreementStep } from "./steps/TermsAgreementStep";
import { VerificationStep } from "./steps/VerificationStep";
import { PaymentSetupStep } from "./steps/PaymentSetupStep";
import { useGetOnboardingStatusQuery } from "components/Slices/usersApiSlice";

const steps = [
  { label: "Welcome", icon: <HandshakeIcon /> },
  { label: "Contact Info", icon: <ContactMailIcon /> },
  { label: "Terms", icon: <GavelIcon /> },
  { label: "Verify", icon: <VerifiedUserIcon /> },
  { label: "Payment", icon: <AccountBalanceIcon /> },
];

const READ_ONLY_STATUSES = ["verifying", "active", "complete"];

export default function OnboardingPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const urlStep = parseInt(searchParams.get("step") || "0", 10);
  const sanitizedUrlStep =
    isNaN(urlStep) || urlStep < 0 || urlStep > 4 ? 0 : urlStep;
  const [activeStep, setActiveStep] = useState(sanitizedUrlStep);

  const { data: onboardingData, isLoading } = useGetOnboardingStatusQuery();

  const isReadOnly = useMemo(() => {
    const status = onboardingData?.status?.toLowerCase();
    return READ_ONLY_STATUSES.includes(status || "");
  }, [onboardingData]);

  // Determines how far into the flow the user is allowed to navigate based on backend data.
  // Steps 1-2 are always open (contact + DOB are saved together).
  // Step 3-4 (Terms, Verification) require contact info to have been saved.
  // Step 5 (Payment) requires verification to have been attempted.
  const maxAllowedStep = useMemo(() => {
    if (!onboardingData) return 2;
    const status = onboardingData.status?.toLowerCase();
    if (
      ["verifying", "active", "complete", "verification_failed"].includes(
        status || "",
      )
    ) {
      return 4;
    }
    if (
      onboardingData.hasAddress &&
      onboardingData.hasDob &&
      onboardingData.hasPhone
    ) {
      // pending users who've completed contact info can skip verification and go to payment
      return 4;
    }
    return 1;
  }, [onboardingData]);

  useEffect(() => {
    if (!onboardingData) return;
    const status = onboardingData?.status?.toLowerCase();
    if (
      status === "active" ||
      status === "complete" ||
      status === "verifying" ||
      status === "verification_failed"
    ) {
      setActiveStep(3);
      return;
    }
    // Clamp URL-requested step to what the user has actually completed
    setActiveStep((prev) => Math.min(prev, maxAllowedStep));
  }, [onboardingData, maxAllowedStep]);

  const SESSION_KEY = "onboarding_form";

  const methods = useForm<FullOnboardingForm>({
    mode: "onChange",
    resolver: zodResolver(fullOnboardingSchema),
    defaultValues: (() => {
      // Restore any in-progress form data saved before the user navigated away.
      // This is only used before contact info is submitted (after that, Stripe
      // data is the source of truth and the effect below populates the form).
      try {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) return JSON.parse(saved);
      } catch {}
      return {
        address: { line1: "", line2: "", city: "", state: "", postal_code: "" },
        country: "CA",
        phone: "",
        dateOfBirth: "",
      };
    })(),
  });

  // Persist form values to sessionStorage while contact info hasn't been saved yet.
  useEffect(() => {
    if (onboardingData?.hasAddress) return; // Stripe is source of truth once saved
    const subscription = methods.watch((values) => {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(values));
      } catch {}
    });
    return () => subscription.unsubscribe();
  }, [methods, onboardingData?.hasAddress]);

  // Clear persisted draft once contact info is confirmed saved on the backend.
  useEffect(() => {
    if (onboardingData?.hasAddress && onboardingData?.hasPhone) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [onboardingData?.hasAddress, onboardingData?.hasPhone]);

  useEffect(() => {
    if (onboardingData?.stripeData) {
      const stripeData = onboardingData.stripeData;
      const formData: Partial<FullOnboardingForm> = {};

      if (stripeData.address) {
        formData.address = {
          line1: stripeData.address.line1 || "",
          line2: stripeData.address.line2 || "",
          city: stripeData.address.city || "",
          state: stripeData.address.state || "",
          postal_code: stripeData.address.postal_code || "",
        };
      }

      if (onboardingData.country) {
        formData.country = onboardingData.country;
      }

      if (stripeData.phone) {
        const phone = stripeData.phone.replace(/^\+1/, "");
        formData.phone = phone;
      }

      if (stripeData.dob?.formatted) {
        formData.dateOfBirth = stripeData.dob.formatted;
      }

      methods.reset({ ...methods.getValues(), ...formData });
    }
  }, [onboardingData, methods]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;
      case 1:
        return <ContactInfoStep onNext={handleNext} onBack={handleBack} isReadOnly={isReadOnly} countryLocked={!!onboardingData?.country} />;
      case 2:
        return (
          <TermsAgreementStep
            onNext={handleNext}
            onBack={handleBack}
            isReadOnly={isReadOnly}
          />
        );
      case 3:
        return (
          <VerificationStep
            onNext={handleNext}
            onBack={handleBack}
            isReadOnly={isReadOnly}
            verificationStatus={onboardingData?.status?.toLowerCase()}
          />
        );
      case 4:
        return <PaymentSetupStep onBack={handleBack} bankSkipped={onboardingData?.bankSkipped} userStatus={onboardingData?.status?.toLowerCase()} />;
      default:
        return null;
    }
  };

  // Get status display info
  const getStatusInfo = () => {
    const status = onboardingData?.status?.toLowerCase();
    if (status === "verifying") {
      return {
        label: "Verification In Progress",
        icon: <HourglassEmptyIcon />,
        color: "warning" as const,
        message:
          "Your identity verification is being processed. This usually takes a few minutes.",
      };
    }
    if (status === "active" || status === "complete") {
      return {
        label: "Verified",
        icon: <CheckCircleIcon />,
        color: "success" as const,
        message: "Your account is fully verified. You're all set!",
      };
    }
    if (status === "verification_failed") {
      return {
        label: "Verification Failed",
        icon: <ErrorOutlineIcon />,
        color: "error" as const,
        message:
          "Your identity verification was unsuccessful. Please try again with clear photos of your ID.",
      };
    }
    return null;
  };

  const statusInfo = getStatusInfo();

  // On the Welcome step (step 0), show a simplified layout without stepper
  const isWelcomeStep = activeStep === 0;

  return (
    <Box
      sx={{
        minHeight: "100%",
        pt: { xs: 1.5, sm: 2.5 },
        pb: 4,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.02,
        )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="md">
        {/* How It Works Link — hidden on welcome step */}
        {!isWelcomeStep && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              borderRadius: 2,
            }}
          >
            <InfoIcon fontSize="small" sx={{ color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary">
              New to Sanduuq?{" "}
              <Link
                component={RouterLink}
                to="/how-it-works"
                target="_blank"
                sx={{ fontWeight: 600 }}
              >
                Learn how lending circles work
              </Link>
            </Typography>
          </Paper>
        )}

        {/* Status Banner — hidden on welcome step */}
        {!isWelcomeStep && statusInfo && (
          <Alert
            severity={statusInfo.color}
            icon={statusInfo.icon}
            sx={{
              mb: 2,
              borderRadius: 2,
              "& .MuiAlert-message": {
                display: "flex",
                alignItems: "center",
                gap: 1,
              },
            }}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {statusInfo.label}
              </Typography>
              <Typography variant="body2">{statusInfo.message}</Typography>
            </Box>
          </Alert>
        )}

        {/* Stepper — hidden on welcome step, labels hidden on mobile */}
        {!isWelcomeStep && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      onClick={() => index < activeStep && setActiveStep(index)}
                      sx={{
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor:
                          index <= activeStep
                            ? theme.palette.primary.main
                            : alpha(theme.palette.grey[400], 0.3),
                        color: index <= activeStep ? "white" : "grey.500",
                        transition: "all 0.3s ease",
                        transform:
                          index === activeStep ? "scale(1.1)" : "scale(1)",
                        boxShadow:
                          index === activeStep
                            ? `0 4px 14px ${alpha(
                                theme.palette.primary.main,
                                0.4,
                              )}`
                            : "none",
                        cursor: index < activeStep ? "pointer" : "default",
                      }}
                    >
                      {React.cloneElement(step.icon, {
                        sx: { fontSize: { xs: 16, sm: 20 } },
                      })}
                    </Box>
                  )}
                >
                  {/* Always show the label — an icon-only dot doesn't tell
                      someone unfamiliar with the app what step they're on.
                      Smaller on mobile so 5 labels still fit across the row. */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: "0.62rem", sm: "0.82rem" },
                      fontWeight: index === activeStep ? 600 : 400,
                      color:
                        index <= activeStep
                          ? "text.primary"
                          : "text.secondary",
                      textAlign: "center",
                    }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            p: { xs: 2.5, sm: 3, md: 3.5 },
            bgcolor: "background.paper",
          }}
        >
          <FormProvider {...methods}>{renderStepContent()}</FormProvider>
        </Paper>

        {/* Security Note */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2, textAlign: "left" }}
        >
          Your information is encrypted and securely stored.
        </Typography>
      </Container>
    </Box>
  );
}
